import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Add,
  GroupsOutlined,
  KeyboardArrowDown,
  Remove,
} from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonBase,
  Divider,
  IconButton,
  Popover,
  TextField,
  Typography,
} from '@mui/material';

import {
  MAX_SEARCH_PASSENGERS,
  SearchPassengerDraft,
  SearchPassengerTypeRule,
  resizeSearchPassengerType,
  searchPassengerCount,
  updateSearchPassengerAge,
  validateSearchPassengers,
} from '../searchPassengers';
import { releaseNumberInputOnWheel } from '../numberInput';

interface Props {
  value: SearchPassengerDraft[];
  onChange: (passengers: SearchPassengerDraft[]) => void;
  rules: readonly SearchPassengerTypeRule[];
  maxPassengers?: number;
}

/**
 * Selector único de composición para los buscadores. El aspecto es siempre el
 * mismo; `rules` expresa únicamente lo que cambia por contrato: tipos
 * disponibles y si su edad está oculta, es opcional o es obligatoria.
 */
export function SearchPassengerPicker({
  value,
  onChange,
  rules,
  maxPassengers = MAX_SEARCH_PASSENGERS,
}: Props) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const validation = validateSearchPassengers(value, rules, maxPassengers);
  const incomplete = rules.length > 0 && !validation.valid;
  const summary = rules
    .map((rule) => {
      const passengers = value.filter((passenger) => passenger.type === rule.type);
      if (!passengers.length) return undefined;
      const ages = passengers
        .map((passenger) => passenger.age.trim())
        .filter(Boolean);
      return `${t(`travelServices.passengerPicker.types.${rule.type}`, {
        count: passengers.length,
      })}${ages.length ? ` (${ages.join(', ')})` : ''}`;
    })
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <ButtonBase
        aria-label={t('travelServices.passengerPicker.label')}
        aria-expanded={Boolean(anchorEl)}
        aria-haspopup="dialog"
        aria-invalid={incomplete}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={(theme) => ({
          width: '100%',
          minWidth: 0,
          minHeight: 28,
          display: 'flex',
          justifyContent: 'flex-start',
          gap: 0.75,
          borderRadius: 0.5,
          textAlign: 'left',
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        })}
      >
        <GroupsOutlined fontSize="small" color="action" />
        <Typography
          variant="body2"
          fontWeight={600}
          noWrap
          color={incomplete ? 'error.main' : 'text.primary'}
          sx={{ minWidth: 0, flex: 1 }}
          title={
            incomplete
              ? `${summary} · ${t('travelServices.passengerPicker.incomplete')}`
              : summary
          }
        >
          {incomplete
            ? `${summary || t('travelServices.passengerPicker.empty')} · ${t(
              'travelServices.passengerPicker.incomplete',
            )}`
            : summary || t('travelServices.passengerPicker.empty')}
        </Typography>
        <KeyboardArrowDown fontSize="small" color="action" />
      </ButtonBase>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: { xs: 'calc(100vw - 32px)', sm: 380 },
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'min(620px, calc(100vh - 32px))',
            mt: 0.75,
            borderRadius: 2,
          },
        }}
      >
        <Box p={2} display="flex" flexDirection="column" gap={1.5}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {t('travelServices.passengerPicker.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('travelServices.passengerPicker.max', { count: maxPassengers })}
            </Typography>
          </Box>

          {rules.map((rule, ruleIndex) => {
            const count = searchPassengerCount(value, rule.type);
            const available = maxPassengers - (value.length - count);
            const entries = value
              .map((passenger, index) => ({ passenger, index }))
              .filter(({ passenger }) => passenger.type === rule.type);
            return (
              <Box key={rule.type} display="flex" flexDirection="column" gap={1}>
                {ruleIndex > 0 && <Divider />}
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                  <Box minWidth={0}>
                    <Typography variant="body2" fontWeight={600}>
                      {t(`travelServices.passengerPicker.typeNames.${rule.type}`)}
                    </Typography>
                    {rule.age !== 'hidden' && (
                      <Typography variant="caption" color="text.secondary">
                        {t(`travelServices.passengerPicker.age.${rule.age}`)}
                      </Typography>
                    )}
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <IconButton
                      size="small"
                      aria-label={t('travelServices.passengerPicker.decrease', {
                        type: t(`travelServices.passengerPicker.typeNames.${rule.type}`),
                      })}
                      disabled={count <= (rule.minCount ?? 0)}
                      onClick={() =>
                        onChange(
                          resizeSearchPassengerType(
                            value,
                            rule.type,
                            count - 1,
                            rules,
                            maxPassengers,
                          ),
                        )
                      }
                    >
                      <Remove fontSize="small" />
                    </IconButton>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      textAlign="center"
                      sx={{ width: 24 }}
                    >
                      {count}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label={t('travelServices.passengerPicker.increase', {
                        type: t(`travelServices.passengerPicker.typeNames.${rule.type}`),
                      })}
                      disabled={count >= available}
                      onClick={() =>
                        onChange(
                          resizeSearchPassengerType(
                            value,
                            rule.type,
                            count + 1,
                            rules,
                            maxPassengers,
                          ),
                        )
                      }
                    >
                      <Add fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {rule.age !== 'hidden' && entries.length > 0 && (
                  <Box
                    display="grid"
                    gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
                    gap={1}
                  >
                    {entries.map(({ passenger, index }, typeIndex) => {
                      const error = validation.errors[index];
                      return (
                        <TextField
                          key={`${rule.type}-${typeIndex}`}
                          size="small"
                          type="number"
                          label={t('travelServices.passengerPicker.ageLabel', {
                            type: t(`travelServices.passengerPicker.typeNames.${rule.type}`),
                            number: typeIndex + 1,
                          })}
                          value={passenger.age}
                          inputProps={{
                            min: rule.minAge ?? 0,
                            max: rule.maxAge ?? 120,
                            onWheel: releaseNumberInputOnWheel,
                          }}
                          required={rule.age === 'required'}
                          error={Boolean(error)}
                          helperText={
                            error
                              ? t(`travelServices.passengerPicker.errors.${error}`)
                              : t(`travelServices.passengerPicker.age.${rule.age}`)
                          }
                          onChange={(event) =>
                            onChange(
                              updateSearchPassengerAge(value, index, event.target.value),
                            )
                          }
                        />
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}

          <Box display="flex" justifyContent="flex-end" pt={0.5}>
            <Button
              variant="contained"
              disabled={!validation.valid}
              onClick={() => setAnchorEl(null)}
            >
              {t('travelServices.passengerPicker.done')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
